import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { AlertTriangle, Wrench } from 'lucide-react';
import { cn } from '../ui/utils';
import { Checkbox } from '../ui/checkbox';

interface MaintenanceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: { reason: string; notes: string; createTicket: boolean }) => void;
    wellName: string;
    isDarkMode: boolean; // Dialogs usually handle theme via generic providers but explicit here if needed
}

export function MaintenanceModal({
    isOpen,
    onClose,
    onSubmit,
    wellName,
    isDarkMode
}: MaintenanceModalProps) {
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const [createTicket, setCreateTicket] = useState(false);

    const handleSubmit = () => {
        if (!reason.trim()) return;
        onSubmit({ reason, notes, createTicket });
        // Reset
        setReason('');
        setNotes('');
        setCreateTicket(false);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className={cn("sm:max-w-[425px]", isDarkMode ? "bg-slate-900 border-slate-800 text-white" : "")}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-amber-500">
                        <AlertTriangle className="w-5 h-5" />
                        Maintenance Mode
                    </DialogTitle>
                    <DialogDescription className={isDarkMode ? "text-slate-400" : ""}>
                        Set status of <strong>{wellName}</strong> to Maintenance. This will alert admins and pause routine alerts.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                        <Label htmlFor="reason" className={isDarkMode ? "text-slate-300" : ""}>Reason for Maintenance</Label>
                        <Input
                            id="reason"
                            placeholder="e.g. Pump Failure, Leak Repair"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className={isDarkMode ? "bg-slate-800 border-slate-700" : ""}
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="notes" className={isDarkMode ? "text-slate-300" : ""}>Additional Notes</Label>
                        <Textarea
                            id="notes"
                            placeholder="Describe the issue and required actions..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className={cn("min-h-[100px]", isDarkMode ? "bg-slate-800 border-slate-700" : "")}
                        />
                    </div>
                    <div className="flex items-center space-x-2 pt-2">
                        <Checkbox
                            id="ticket"
                            checked={createTicket}
                            onCheckedChange={(c) => setCreateTicket(!!c)}
                            className={isDarkMode ? "border-slate-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white" : ""}
                        />
                        <Label
                            htmlFor="ticket"
                            className={cn("text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", isDarkMode ? "text-slate-300" : "")}
                        >
                            Create Service Ticket automatically
                        </Label>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className={isDarkMode ? "bg-transparent border-slate-700 hover:bg-slate-800 text-slate-300" : ""}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        disabled={!reason.trim()}
                    >
                        <Wrench className="w-4 h-4 mr-2" />
                        Set Status
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
